import { HStack, VStack, Center } from "@gluestack-ui/themed";
import { Text } from "@repo/core/components/core/Text";
import { applyMaskToValue } from "@repo/core/utils/mask.utils";
import { sanitizeNameInput } from "@repo/core/utils/string.utils";
import { usePreProposal } from "../../hooks/usePreProposal";

import { InfoSecurityController } from "./controller";
import { InputControlled } from "@repo/core/components/forms/InputControlled";
import { ScaffoldFooter } from "@repo/core/components/layout/Scaffolds/components/ScaffoldFooter";
import { PageScaffold } from "@repo/core/components/layout/Scaffolds/PageScaffold";
import { ActionSheet } from "@repo/core/components/layout/Actionsheet";
import { Button } from "@repo/core/components/core/Button";
import { Icon } from "@repo/core/components/core/Icon";
import { isReanalysisProposal } from "../BiometryValidation/services/navigation.service";

export const InfoSecurity = () => {
  const {
    control,
    back,
    handleOnSubmitForm,
    reanalysisActionSheetRef,
    handleConfirmReanalysis,
    handleCancelReanalysis,
    phoneUpdateActionSheetRef,
    handleKeepNumber,
    handleChangeNumber,
    isValidatingCPF,
    handleCPFBlur,
    isCreatingReanalysis,
    isKeepingCurrentPhone,
    isCpfLocked,
    isProcessingInfoSecurity
  } = InfoSecurityController();

  const { proposalData } = usePreProposal();
  const formattedPhone = applyMaskToValue(
    `${proposalData?.strEndDdd1 ?? ""}${proposalData?.strEndFone1 ?? ""}`,
    "cellphone"
  );

  return (
    <>
      <PageScaffold
        onBackPress={back}
        onPressRightIcon={back}
        rightWebLabel="Fechar"
        title="Dados de Segurança"
        footerContent={
          <ScaffoldFooter
            primaryText="Continuar"
            primaryAction="primary"
            primaryIsLoading={isProcessingInfoSecurity}
            primaryIsDisabled={isProcessingInfoSecurity}
            primaryOnPress={handleOnSubmitForm}
            secondaryIsDisabled={isProcessingInfoSecurity}
            secondaryText="Cancelar"
            secondaryOnPress={back}
          />
        }
      >
        <HStack mb="$6">
          <Text fontSize="$md" fontWeight={500} color="$textLight800">
            Para iniciar, insira os dados abaixo:
          </Text>
        </HStack>

        <VStack w="$full" gap="$4">
          <InputControlled
            control={control}
            name="cpf"
            placeholder="Digite o CPF"
            size="md"
            mask="cpf"
            keyboardType="number-pad"
            returnKeyType="next"
            label="CPF"
            isRequired
            isLoading={isValidatingCPF}
            isDisabled={isCpfLocked}
            onBlur={handleCPFBlur}
          />

          <InputControlled
            control={control}
            name="fullName"
            placeholder="Digite o nome completo"
            maxLength={100}
            size="md"
            label="Nome completo"
            returnKeyType="done"
            isRequired
            transformValue={sanitizeNameInput}
          />
        </VStack>
      </PageScaffold>

      {!isReanalysisProposal(proposalData) && (
        <ActionSheet ref={reanalysisActionSheetRef} backdropCanClose={false}>
          <VStack w="$full" py="$6" px="$4" gap="$6" alignItems="center">
            <Center>
              <Icon name="badge-check" size={48} color="$primary500" />
            </Center>

            <VStack gap="$3" alignItems="center">
              <Text
                fontSize="$xl"
                fontWeight={700}
                color="$textLight900"
                textAlign="center"
              >
                Cliente já possui cartão WayUp
              </Text>
              <Text
                fontSize="$md"
                color="$textLight600"
                textAlign="center"
                px="$4"
              >
                O CPF informado já está associado a um cliente. Você pode
                prosseguir com uma reavaliação de cliente para análise de
                crédito, aumento de limite ou desbloqueio do cartão.
              </Text>
            </VStack>

            <VStack w="$full" gap="$3" mt="$2">
              <Button
                width="$full"
                action="primary"
                text="Solicitar reavaliação"
                onPress={handleConfirmReanalysis}
                isLoading={isCreatingReanalysis}
                isDisabled={isCreatingReanalysis}
                leftIcon={{
                  name: "magnifying-glass-dollar",
                  size: "sm",
                  color: "$textLight0"
                }}
              />
              <Button
                size="lg"
                width="$full"
                variant="outline"
                action="darker-secondary"
                onPress={handleCancelReanalysis}
                text="Cancelar"
                isDisabled={isCreatingReanalysis}
              />
            </VStack>
          </VStack>
        </ActionSheet>
      )}

      <ActionSheet ref={phoneUpdateActionSheetRef} backdropCanClose={false}>
        <VStack w="$full" py="$6" px="$4" gap="$6" alignItems="center">
          <Center>
            <Icon name="badge-check" size={48} color="$primary500" />
          </Center>

          <VStack gap="$3" alignItems="center">
            <Text
              fontSize="$xl"
              fontWeight={700}
              color="$textLight900"
              textAlign="center"
            >
              Atualização Cadastral
            </Text>
            <Text
              fontSize="$md"
              color="$textLight600"
              textAlign="center"
              px="$4"
            >
              O número abaixo receberá os códigos de segurança e notificações do
              seu cartão. Deseja manter este número ou cadastrar um novo?
            </Text>
            {formattedPhone ? (
              <Text
                fontSize="$md"
                fontWeight={700}
                color="$textLight900"
                textAlign="center"
              >
                {formattedPhone}
              </Text>
            ) : null}
          </VStack>

          <VStack w="$full" gap="$3" mt="$2">
            <Button
              width="$full"
              action="primary"
              text="Manter número"
              onPress={handleKeepNumber}
              isLoading={isKeepingCurrentPhone}
              isDisabled={isKeepingCurrentPhone}
            />
            <Button
              size="lg"
              width="$full"
              variant="outline"
              action="darker-secondary"
              onPress={handleChangeNumber}
              text="Alterar para novo"
              isDisabled={isKeepingCurrentPhone}
            />
          </VStack>
        </VStack>
      </ActionSheet>
    </>
  );
};
